import React, { useEffect, useState } from 'react';
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View
} from '@react-pdf/renderer';
import { EFlyerPDF } from '../EFlyerPDF';
import { ProductsContextType } from './ProductsContextType';
import { useEFlyerQRCode } from '../QRCallToAction';
import { EFlyerFooterPDF, useHydrateFooter } from '../EFlyerFooter';
import { handleImageContent } from '../eflyer-utils.js';
import { get } from 'lodash';

/** @typedef {import('./ProductsContextType').ProductsContext} ProductsContext */

/**
 *
 * @param {{context: ProductsContext}} props
 * @returns {React.JSX.Element|null}
 * @constructor
 */
export const ProductsPdf = ({ context }) => {
  const { qr } = useEFlyerQRCode(context.qrUrl);
  const footerData = useHydrateFooter(context.footer);

  // The PDF will not be re-rendered, so ensure all data is available before rendering
  if (
    !qr ||
    !footerData.ready ||
    !context.productsLoaded ||
    context.prices.size === 0
  ) {
    return null;
  }

  return (
    <EFlyerPDF>
      <ProductsPdfDocument context={context} qr={qr} footerData={footerData} />
    </EFlyerPDF>
  );
};

ProductsPdf.propTypes = {
  context: ProductsContextType.isRequired
};

const ProductsPdfDocument = ({ context, qr, footerData }) => {
  const { themeColors } = context;
  const styles = StyleSheet.create({
    page: {
      padding: 10
    },
    headerContainer: {
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: 12,
      paddingBottom: 12,
      height: '12%',
      borderBottom: '5px solid ' + themeColors.color500
    },
    logo: {
      maxWidth: '80%',
      marginLeft: 24,
      objectFit: 'contain',
      paddingVertical: 10
    },
    headings: { paddingLeft: 36 },
    heading: {
      fontSize: 32,
      color: themeColors.color500
    },
    mainContainer: {
      paddingLeft: 30,
      paddingRight: 30,
      height: '61%'
    },
    introText: {
      fontSize: 15,
      height: '14%',
      borderBottomColor: '#E5E7EB',
      borderBottomWidth: 1,
      paddingVertical: 24,
      paddingHorizontal: 12
    },
    productsGrid: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      marginTop: 32,
      height: '66%',
      columnGap: 1,
      rowGap: 12
    },
    productCard: {
      width: '23%',
      height: 125 // 3 rows
    },
    productImage: {
      height: 55,
      marginBottom: 6,
      width: '100%',
      objectFit: 'contain'
    },
    productName: {
      height: 14,
      fontSize: 10,
      maxLines: 1,
      fontWeight: 'bold',
      color: '#374151'
    },
    productDescription: {
      height: 36,
      paddingVertical: 1,
      fontSize: 10,
      color: '#4B5563'
    },
    priceRow: {
      height: 16,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: 6
    },
    price: {
      fontSize: 10,
      color: '#111827',
      fontWeight: 'bold'
    },
    originalPrice: {
      fontSize: 10,
      color: '#6B7280',
      textDecoration: 'line-through',
      marginLeft: 6
    },
    callToActionContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      height: '15%',
      paddingLeft: 30,
      paddingRight: 30
    },
    qrCode: {
      height: 60,
      width: 60,
      marginRight: 10
    },
    callToAction: {
      fontWeight: 'bold',
      fontSize: 12
    },
    url: {
      fontSize: 12
    },
    footerContainer: {
      backgroundColor: themeColors.color500,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '12%'
    },
    wHalf: { width: '50%' }
  });

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.headerContainer}>
          <View style={styles.wHalf}>
            <Image src={context.logoUrl} style={styles.logo} />
          </View>
          <View style={[styles.wHalf, styles.headings]}>
            <Text style={styles.heading}>{context.promoHeader}</Text>
          </View>
        </View>
        <View style={styles.mainContainer}>
          <Text style={styles.introText}>{context.introText}</Text>
          {Array.isArray(context.productIds) &&
          context.productIds.length > 0 ? (
            <View style={styles.productsGrid}>
              {context.productIds.map((pid, idx) => {
                const p = context.fetchedProducts.get(pid);
                const pricing = context.prices.get(pid);
                if (!p) return null;
                return (
                  <ProductPdfItem
                    key={p?.id || p?.sku || pid || idx}
                    product={p}
                    pricing={pricing}
                    idx={idx}
                    styles={styles}
                  />
                );
              })}
            </View>
          ) : null}
        </View>
        <View style={styles.callToActionContainer}>
          <View>
            <Image src={qr} style={styles.qrCode} />
          </View>
          <View>
            <Text style={styles.callToAction}>{context.callToAction}</Text>
            <Text style={styles.url}>{context.qrUrl}</Text>
          </View>
        </View>
        <View style={styles.footerContainer}>
          <EFlyerFooterPDF footerData={footerData} />
        </View>
      </Page>
    </Document>
  );
};

ProductsPdfDocument.propTypes = {
  context: ProductsContextType
};

/**
 * Renders the PDF document for the Products template
 * @param {{context: ProductsContext}} props
 * @param {string} qr The data url of the generated QR code
 * @returns {React.JSX.Element}
 */
const ProductPdfItem = ({ product, pricing, styles }) => {
  const p = product || {};
  const imageUrl = handleImageContent(get(p, 'primaryAsset'));
  const description = p && p.description ? String(p.description) : '';
  const priceText =
    pricing && pricing.price
      ? formatCurrency(pricing.price.amount, pricing.price.currency)
      : '';
  const originalPriceText =
    pricing && pricing.originalPrice && pricing.originalPrice < pricing.price
      ? formatCurrency(
          pricing.originalPrice.amount,
          pricing.originalPrice.currency
        )
      : '';
  return (
    <View style={styles.productCard}>
      <PdfSmartImage src={imageUrl} style={styles.productImage} />
      {p?.name ? <Text style={styles.productName}>{p.name}</Text> : null}
      {description ? (
        <Text style={styles.productDescription}>
          {description.toLowerCase()}
        </Text>
      ) : null}
      <View style={styles.priceRow}>
        {priceText ? <Text style={styles.price}>{priceText}</Text> : null}
        {originalPriceText ? (
          <Text style={styles.originalPrice}>{originalPriceText}</Text>
        ) : null}
      </View>
    </View>
  );
};

function formatCurrency(amount, currency = 'USD') {
  try {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  } catch (e) {
    const num = typeof amount === 'number' ? amount : parseFloat(amount || '0');
    return `$${num.toFixed(2)}`;
  }
}

/**
 * Wrapper around react-pdf's <Image> that converts GIF sources to PNG in the browser.
 */
const PdfSmartImage = ({ src, style, ...rest }) => {
  const [resolvedSrc, setResolvedSrc] = useState(src);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const converted = await gifToPngDataUrl(src);
      if (mounted) setResolvedSrc(converted);
    })();
    return () => {
      mounted = false;
    };
  }, [src]);

  return <Image src={resolvedSrc} style={style} {...rest} />;
};

/**
 * Convert a GIF image to a PNG data URL by capturing the first frame via Canvas.
 * @param {string|undefined|null} src The image source URL
 * @returns {Promise<string>} PNG data URL or the original src if conversion isn't needed
 */
async function gifToPngDataUrl(src) {
  try {
    const isGif =
      src && typeof src === 'string' && src.toLowerCase().includes('.gif');
    if (!isGif) return src;

    const img = new window.Image();
    // Set crossOrigin to try to avoid tainted canvas when served with proper CORS headers
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = src;

    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
    });

    // Draw onto an offscreen canvas
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;

    ctx.drawImage(img, 0, 0);

    const dataUrl = canvas.toDataURL('image/png');
    // Fallback to original src if something odd happened
    return typeof dataUrl === 'string' && dataUrl.startsWith('data:image/png')
      ? dataUrl
      : src;
  } catch (e) {
    // Likely a CORS tainting issue or other error; fall back to original src
    return src || '';
  }
}

export default ProductsPdf;
